import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import i18n from 'i18next';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import { Button } from '../../../common/components/buttons';
import useStyles from './MissionList.styles';
import useAgent from '../../../common/hooks/useAgent';
import SharedRowColumn from './columns/SharedRowColumn';
import missionAgent from '../../agents/missionAgent';
import { Table } from '../../../table/components';
import { defaultTake, fullPageTableHeight } from '../../../table/components/Table';
import { Container } from '../../../common/components/layout';
import ConfirmationModal from '../../../common/components/modals/ConfirmationModal';
import usePaginatedAgent from '../../../common/hooks/usePaginatedAgent';
import { ContainerLoader } from '../../../common/components/loaders';
import Can, { useAbilityContext } from '../../../common/contexts/abilityContext';
import { downloadFile } from '../../../common/utils/fileMethods';
import { SearchBox } from '../../../common/components/inputs';

const DownloadLoader = () => <CircularProgress size={20} style={{ marginRight: '0.6rem', verticalAlign: 'middle' }} />;

const MissionList = ({ onRowClick, onCreateClick }) => {
  const classes = useStyles();

  const [missionName, setMissionName] = useState();

  const [downloadMission, downloadedMission, isDownloadingMission] = useAgent(missionAgent.downloadMission);

  const [selectedMissionToDelete, setSelectedMissionToDelete] = useState();
  const [searchInputValue, setSearchInputValue] = useState();

  const [deleteMission, deletedMission] = useAgent(missionAgent.deleteMission);
  const [fetchMissions, missions] = usePaginatedAgent(missionAgent.fetchMissions);

  const [showModal, hideModal] = useModal(
    () => (
      <ConfirmationModal
        closeModal={hideModal}
        onCancelClick={() => setSelectedMissionToDelete(undefined)}
        onConfirmClick={() => deleteMission(selectedMissionToDelete.id)}
        confirmButtonLabel={i18n.t('common.button.confirm')}>
        {i18n.t('mission.message.deletionConfirmation')}
      </ConfirmationModal>
    ),
    [selectedMissionToDelete]
  );

  const handleDeleteClick = useCallback((callback) => {
    setSelectedMissionToDelete(callback);
  }, []);

  const handleDownloadClick = useCallback(
    (selectedMission) => {
      setMissionName(selectedMission.name);
      downloadMission(selectedMission.id);
    },
    [downloadMission]
  );

  useEffect(() => {
    if (!isDownloadingMission && downloadedMission && missionName) {
      downloadFile(downloadedMission, missionName, 'json', () => setMissionName(undefined));
    }
  }, [downloadedMission, isDownloadingMission, missionName]);

  useEffect(() => {
    fetchMissions({ take: defaultTake, query: searchInputValue });
  }, [fetchMissions, searchInputValue]);

  useEffect(() => {
    if (selectedMissionToDelete) {
      showModal();
    }
  }, [selectedMissionToDelete, showModal]);

  useEffect(() => {
    if (deletedMission) {
      toast.success(i18n.t('common.message.success.deleted', { entity: 'mission' }));
      fetchMissions({ take: defaultTake, query: searchInputValue });
    }
  }, [deletedMission, fetchMissions, searchInputValue]);

  const context = useAbilityContext();
  const defaultColumns = [
    { Header: i18n.t('mission.id'), accessor: 'id', columnWidth: 4 },
    { Header: i18n.t('mission.name'), accessor: 'name' },
    {
      Header: i18n.t('mission.daysSinceFlown'),
      accessor: (row) => (row.daysSinceFlown === 0 ? i18n.t('mission.notFlown') : row.daysSinceFlown),
    },
  ];

  const { current: columns } = useRef(
    context.can('create', 'client')
      ? defaultColumns.concat([
          {
            Header: i18n.t('common.entity.client'),
            accessor: 'clientName',
          },
          {
            Header: i18n.t('mission.sharedROW'),
            accessor: 'sharedRow',
            Cell: function SharedRowCell(cell) {
              return <SharedRowColumn sharedRow={cell.value} />;
            },
          },
        ])
      : defaultColumns
  );

  const rowActions = useMemo(() => {
    const actions = [
      isDownloadingMission
        ? { render: DownloadLoader }
        : {
            icon: 'download',
            onClick: handleDownloadClick,
          },
    ];
    return context.can('delete', 'mission')
      ? actions.concat({
          icon: 'delete',
          onClick: handleDeleteClick,
        })
      : actions;
  }, [isDownloadingMission, handleDownloadClick, handleDeleteClick, context]);

  return (
    <Container>
      <div className={classes.buttonContainer}>
        <SearchBox onChange={setSearchInputValue} searchInputValue={searchInputValue} />
        <Can I="create" a="mission">
          <Button primary onClick={onCreateClick}>
            {i18n.t('mission.createMission')}
          </Button>
        </Can>
      </div>
      {missions ? (
        <div className={classes.table}>
          <Table
            columns={columns}
            data={missions}
            height={fullPageTableHeight}
            rowActions={rowActions}
            onPageChange={fetchMissions}
            onRowClick={isDownloadingMission ? () => undefined : onRowClick}
          />
        </div>
      ) : (
        <ContainerLoader />
      )}
    </Container>
  );
};

MissionList.propTypes = {
  onRowClick: PropTypes.func,
  onCreateClick: PropTypes.func,
};

MissionList.defaultProps = {
  onRowClick: undefined,
  onCreateClick: undefined,
};

export default MissionList;
